/**
 * @module
 * The client's open sheet cores (`LonaSdk.Client`): the cores it opened,
 * under every alias they were opened by, and the live `parseSheet` cores;
 * and what leaves with a core when it is closed or torn down: its aliases,
 * its rows-side sheet and its rows in the client-wide registries.
 *
 * Split from client.ts: the client keeps the
 * opens; this keeps the bookkeeping of what is open and its release.
 */

import type { Option } from "@tento-lona/sheets";
import { type RowLocalId, type SheetId, SheetNetworkKey } from "@tento-lona/sheets";
import type { SheetModelCache } from "@tento-lona/sheets/internal-sdk";
import type { SheetHandleCore, SheetHandleDeps } from "./sheet-handle-core.ts";

/** See the module note: a client's open cores and their release. */
export class OpenSheetCores {
  /** The open sheets, under every alias they were opened by. */
  readonly opened = new Map<string, SheetHandleCore>();
  /** The live `parseSheet` cores: never cached for `open`, but torn down
   *  by the client's `dispose` like every other; each leaves on its teardown. */
  readonly parsed = new Set<SheetHandleCore>();
  readonly #client: OpenSheetCores.Client;

  constructor(client: OpenSheetCores.Client) {
    this.#client = client;
  }

  /** Every core, opened or parsed, once each whatever its aliases. */
  all(): Set<SheetHandleCore> {
    return new Set([...this.opened.values(), ...this.parsed]);
  }

  /**
   * Evict a sheet's LIVE core (every `opened` alias) and its rows-side
   * cache. A COW promote registers the core under up to THREE aliases (id,
   * cacheKey and lookupKey, `applyPromotion`), but `sheets.delete` only
   * clears the server-LIST cache. Without this eviction a re-open of the
   * same key returns the stale promoted handle (now `persistence:
   * "synced"`, pointing at the soft-deleted server sheet); editing it
   * no-ops `ensurePromoted` (already synced, `promoter` nulled), so the
   * write lands on the deleted sheet and the sheet can never be re-COWed
   * in-session. Removing every alias and forgetting the rows-side sheet
   * makes the next open re-seed a FRESH ephemeral, which the server's
   * idempotent restore (`create_with_rows_idempotent`) then revives on the
   * re-promote. Wired to `SheetsClient`'s `onSheetDeleted`; a host reaches
   * it through `platformOf(client).closeSheet`.
   */
  close(sheetId: SheetId): void {
    const canonical = sheetId.toString();
    for (const core of this.opened.values()) {
      if (core.id.toString() === canonical) {
        this.evict(core);
        return;
      }
    }
  }

  /**
   * Remove EVERY alias pointing at `core` (id / cacheKey / lookupKey) and
   * drop its rows-side sheet so the next open re-hydrates fresh, keyed by
   * the (promote-preserved) sheet id, the same key `rekeyRows` used. A
   * `parseSheet` core leaves the parsed set (dispose no longer reaches it);
   * a core the cache does not hold (a `parseSheet` preview, one already
   * evicted) changes nothing else. A torn-down core's rows-side sheet and
   * rows are released (`#disposeRowsSide`, `#releaseRowIds`).
   */
  evict(core: SheetHandleCore): void {
    this.parsed.delete(core);
    let cached = false;
    for (const [k, h] of [...this.opened]) {
      if (h !== core) continue;
      this.opened.delete(k);
      cached = true;
    }
    const modelCache = this.#client.modelCache();
    if (cached) modelCache.forget(SheetNetworkKey.fromWire(core.network.id));
    if (core.disposed) {
      this.#disposeRowsSide(core);
      if (!this.#client.disposed()) this.#releaseRowIds(core.rowIds(), core);
    }
  }

  /** A failed parse's rows (`SheetHandleCore.parse`'s `abandoned`): released unless the client is gone. */
  readonly abandonParsed = (rows: Set<RowLocalId>): void => {
    if (!this.#client.disposed()) this.#releaseRowIds(rows, null);
  };

  /**
   * Release `released` from the client's row registry, except the rows a
   * live core other than `core` still places. Without it every closed
   * sheet's rows stayed reachable until the client's `dispose`; a reopen
   * registers them again in its parse. Also the rows of a parse whose core
   * was never built (`core` `null`): no teardown would release them. Not on
   * the client's own `dispose`: the registry goes with the client, and a
   * write still settling during it rolls back through the registry's row.
   */
  #releaseRowIds(released: Set<RowLocalId>, core: Option<SheetHandleCore>): void {
    for (const other of this.all()) {
      if (other === core || other.disposed) continue;
      for (const id of other.rowIds()) released.delete(id);
    }
    this.#client.rows().release(released);
    const modelCache = this.#client.modelCache();
    // Their ledger nodes go too: the ledger is client-wide and kept them.
    modelCache.retireRowNodes(released);
    // A released row that holds no cells in the ordinary store still has its
    // cardinality declared there, keyed by its id: drop it, so nothing keeps
    // the id (the identity registry holds ids weakly). A row with cells is
    // left to the store's budgeted eviction, which drops both; a reopen
    // declares the cardinality again in its parse.
    const ordinary = modelCache.ordinaryCells;
    for (const id of released) if (ordinary.rowData(id) == null) ordinary.forget(id);
  }

  /**
   * After `core` was torn down (its last lease released, the client's
   * `dispose`): dispose its rows-side sheet (the sheet's subscriptions on
   * the client-wide sink, its sync-key leases and its refetch timer), so
   * neither the sink nor the sync watcher keeps the torn-down core (and
   * every cell it held) reachable. Left alone while another live core
   * reads it. A `parseSheet` core's sheet the model cache still holds
   * leaves the cache first, as an opened core's does on eviction: the next
   * open hydrates a fresh one.
   */
  #disposeRowsSide(core: SheetHandleCore): void {
    const sheet = core.rowsSide;
    if (sheet == null) return;
    for (const other of this.all()) {
      if (other !== core && !other.disposed && other.rowsSide === sheet) return;
    }
    const modelCache = this.#client.modelCache();
    const key = SheetNetworkKey.fromWire(core.network.id);
    if (modelCache.peek(key) === sheet) modelCache.forget(key);
    sheet.dispose();
  }
}

/** Types that go with `OpenSheetCores`: `Client`. */
export namespace OpenSheetCores {
  /** What the cores read of their client, read when used (the client builds its registries after this). */
  export interface Client {
    /** The client-wide row registry. */
    rows(): SheetHandleDeps["rows"];
    /** The client's model cache. */
    modelCache(): SheetModelCache;
    /** Whether the client was disposed (its registries go with it: nothing is released then). */
    disposed(): boolean;
  }
}
